/* eslint-disable react/forbid-dom-props */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Image from 'next/future/image';
import { Customer } from '@lambda/apis/src/customer/types';
import { CtaButton } from '@/components/Cta/Cta';
import i18n from '@/settings/i18n';
import SocialLogin, {
  SocialLoginAction,
} from '@/components/accounts/SocialLogin';
import OpenEyeIcon from '@/public/icons/account/eye_open.svg';
import ClosedEyeIcon from '@/public/icons/account/eye_closed.svg';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { trackLogin } from '@/lib/analytics/trackCustomer';
import { loadGoogleSigninScript } from '@/lib/google-signin';

const t = i18n.init();

const schema = yup
  .object()
  .shape({
    email: yup
      .string()
      .email(t`Email must be a valid email`)
      .required(t`Email is a required field`),
    password: yup
      .string()
      .min(6, t`Password must be at least 6 characters`)
      .required(t`Password is a required field`),
  })
  .required();

type Form = yup.Asserts<typeof schema>;

export default function LoginForm(props: {
  header?: JSX.Element;
  onSuccess: (customer: Customer) => void;
}) {
  const form = useForm<Form>({ resolver: yupResolver(schema) });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { login, loading, error } = useCustomerContext();

  useEffect(() => {
    loadGoogleSigninScript();
  }, []);

  const loginAndCallback = async (input: {
    email: string;
    password: string;
  }) => {
    const customer = await login(input.email, input.password);

    if (!customer) return;

    trackLogin();

    props.onSuccess(customer);
  };

  return (
    <>
      {props.header}
      <div className="flex w-full flex-col">
        <input
          className="my-1 rounded-sm border border-solid border-gray-500  p-3 text-gray-700 outline-1 outline-gray-700"
          placeholder={t`Email`}
          type="email"
          {...form.register('email')}
        />
        {form.formState.errors.email && (
          <div className="text-left text-xs text-red">
            {form.formState.errors.email.message}
          </div>
        )}
      </div>
      <div className="relative flex w-full flex-col">
        <input
          className="my-1 rounded-sm border border-solid border-gray-500  p-3 text-gray-700 outline-1 outline-gray-700"
          placeholder={t`Password`}
          type={passwordVisible ? 'text' : 'password'}
          {...form.register('password')}
        />
        <button
          className={
            'absolute right-5 mt-5 flex h-[20px] w-[20px] translate-x-0.5 cursor-pointer items-center justify-center'
          }
          onClick={() => {
            setPasswordVisible(!passwordVisible);
          }}
          type="button"
        >
          <span className="h-[20px] w-[20px]">
            <Image
              src={passwordVisible ? OpenEyeIcon : ClosedEyeIcon}
              width={20}
              height={20}
              alt="Add to Wishlist"
              className="h-full w-[20px] object-contain"
              priority
            />
          </span>
        </button>
        {form.formState.errors.password && (
          <div className="text-left text-xs text-red">
            {form.formState.errors.password.message}
          </div>
        )}
      </div>

      <CtaButton
        type="submit"
        className="my-1 w-full !px-3 !py-2"
        onClick={form.handleSubmit(loginAndCallback)}
        loading={loading}
      >
        {t`Login`}
      </CtaButton>
      {error && <p className="text-sm text-red">{error}</p>}
      <SocialLogin
        action={SocialLoginAction.LOGIN}
        onSuccess={props.onSuccess}
      />
    </>
  );
}
